// DesignWorkCard — Janet's Design Studio proposal, run by one click
// (2026-10-07). She proposes a new brief ("eight painted takes of Henry from
// picks #1 and #5") or one move on one take ("approve #2"); the server has
// already checked every id and take number (api/_lib/janet-design.js). The
// click runs it through the Design Studio's own helpers under RLS — the same
// writes the brief page makes — and then opens the brief.
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Palette, Loader2, ArrowRight, ChevronDown } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { supabase } from '../../lib/supabase'
import { zoomNavigate } from '../../lib/zoomNav'
import { describeDesignPayload, lineageNote } from '../../lib/designStudio'
import {
  createBriefWithTakes, setTakeReference, setTakeStatus, promoteTakeToCanon,
} from '../../hooks/useDesignBriefs'

const DONE_KEY = 'janet.design.done'

// A resumed topic redraws the card; remember what was already run so the
// button can't start the same brief twice.
function proposalKey(p) {
  const raw = p.action === 'set_take_status'
    ? `${p.take_id}:${p.move}`
    : `${p.property_id}:${p.title}:${p.prompt?.length}:${(p.prompt || '').slice(0, 80)}`
  let h = 0
  for (let i = 0; i < raw.length; i++) h = (h * 31 + raw.charCodeAt(i)) | 0
  return String(h)
}
function readDone(key) {
  try { return JSON.parse(localStorage.getItem(DONE_KEY) || '{}')[key] || null } catch { return null }
}
function writeDone(key, value) {
  try {
    const all = JSON.parse(localStorage.getItem(DONE_KEY) || '{}')
    all[key] = value
    localStorage.setItem(DONE_KEY, JSON.stringify(all))
  } catch { /* convenience only */ }
}

const MOVE_BUTTON = { star: 'Star it', unstar: 'Remove the star', candidate: 'Make it a candidate', approved: 'Approve it', canon: 'Make it canon' }

export default function DesignWorkCard({ payload }) {
  const navigate = useNavigate()
  const { user } = useAuth()
  const key = proposalKey(payload)
  const [done, setDone] = useState(() => readDone(key))
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)
  const [showPrompt, setShowPrompt] = useState(false)
  const [prompt, setPrompt] = useState(payload.prompt || '')
  const isBrief = payload.action === 'new_brief'

  const run = async () => {
    if (busy || done) return
    setBusy(true); setErr(null)
    try {
      let briefId = payload.brief_id || null
      if (isBrief) {
        const brief = await createBriefWithTakes({
          property: payload.property_id ? { id: payload.property_id, company_id: payload.company_id } : null,
          project: payload.project_id ? { id: payload.project_id } : null,
          subjectName: payload.subject_name,
          title: payload.title,
          briefText: payload.brief_text || '',
          feedbackContext: lineageNote(payload),
          prompt: prompt.trim(),
          providers: payload.providers,
          variations: payload.variations,
          userId: user?.id,
        })
        briefId = brief.id
      } else if (payload.move === 'star' || payload.move === 'unstar') {
        await setTakeReference(payload.take_id, payload.move === 'star')
      } else if (payload.move === 'canon') {
        const [{ data: take, error: tErr }, { data: brief, error: bErr }] = await Promise.all([
          supabase.from('design_takes').select('id, storage_path, image_url').eq('id', payload.take_id).single(),
          supabase.from('design_briefs').select('id, property_id, company_id, subject_name').eq('id', payload.brief_id).single(),
        ])
        if (tErr || bErr) throw new Error('Could not read the take to make it canon')
        await promoteTakeToCanon({ take, brief, subject: payload.canon_subject, userId: user?.id })
      } else {
        await setTakeStatus(payload.take_id, payload.move)
      }
      const result = { briefId, at: new Date().toISOString() }
      writeDone(key, result)
      setDone(result)
    } catch (e) {
      setErr(e?.message || 'That did not go through')
    } finally {
      setBusy(false)
    }
  }

  const openBrief = () => {
    const id = done?.briefId || payload.brief_id
    if (id) zoomNavigate(navigate, `/studio/design/${id}`)
  }

  const picks = payload.source_takes || []
  const label = describeDesignPayload(payload)

  return (
    <div className="mt-2 border border-warm-200 rounded-xl bg-white overflow-hidden" data-testid="design-work-card">
      <div className="px-3 py-2 bg-warm-50 border-b border-warm-200 flex items-center gap-2">
        <Palette size={12} className="text-warm-600" />
        <span className="text-[11px] font-semibold text-warm-700 uppercase tracking-wide">
          Visual development — {done ? 'done' : 'ready when you are'}
        </span>
      </div>
      <div className="px-3 py-2 space-y-1.5">
        {isBrief ? (
          <>
            <p className="text-xs text-warm-900 font-medium">{payload.title}</p>
            {(payload.property_title || payload.project_title) && (
              <p className="text-[11px] text-warm-500">{[payload.property_title, payload.project_title].filter(Boolean).join(' · ')}</p>
            )}
          </>
        ) : null}
        <p className="text-[11px] text-warm-600">{label}</p>
        {payload.direction && isBrief && <p className="text-[11px] text-warm-500 italic">Direction: {payload.direction}</p>}
        {(picks.length > 0 || payload.image_url) && (
          <div className="flex gap-1.5 pt-0.5">
            {picks.map(t => t.image_url && (
              <figure key={t.take_id} className="w-14">
                <img src={t.image_url} alt={`Take #${t.number}`} className="w-14 h-14 object-cover rounded border border-warm-200" />
                <figcaption className="text-[10px] text-warm-500 text-center">#{t.number}</figcaption>
              </figure>
            ))}
            {!isBrief && payload.image_url && (
              <img src={payload.image_url} alt={`Take #${payload.take_number}`} className="w-20 h-20 object-cover rounded border border-warm-200" />
            )}
          </div>
        )}
        {isBrief && (
          <div>
            <button type="button" onClick={() => setShowPrompt(v => !v)} className="text-[11px] text-warm-600 hover:underline inline-flex items-center gap-1">
              <ChevronDown size={11} className={showPrompt ? 'rotate-180' : ''} /> The prompt every model gets
            </button>
            {showPrompt && (
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                disabled={!!done}
                rows={8}
                className="mt-1 w-full text-[11px] leading-snug text-warm-800 border border-warm-200 rounded p-2 bg-warm-50/50"
              />
            )}
          </div>
        )}
        {err && <p className="text-[11px] text-red-600">{err}</p>}
      </div>
      {done ? (
        <button onClick={openBrief} className="w-full px-3 py-2 bg-warm-100 text-warm-800 text-xs font-semibold hover:bg-warm-200 flex items-center justify-center gap-1.5">
          {isBrief ? 'Started — open the brief' : 'Done — open the brief'} <ArrowRight size={11} />
        </button>
      ) : (
        <button
          onClick={run}
          disabled={busy || (isBrief && !prompt.trim())}
          className="w-full px-3 py-2 bg-navy-800 text-white text-xs font-semibold hover:bg-navy-700 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
        >
          {busy && <Loader2 size={11} className="animate-spin" />}
          {busy ? 'Working…' : isBrief ? 'Start' : MOVE_BUTTON[payload.move] || 'Confirm'}
        </button>
      )}
    </div>
  )
}
